import { Fragment, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import Tag from "@/components/Tag";
import IconButton from "@/components/IconButton";
import Button from "@/components/Button";
import { TABLE_CARD_BODY_CELL, TableCard, TableCardMobileStack, TableCardRow } from "@/components/Table";
import { CardSkeleton } from "@/components/Skeleton";
import EmptyState from "@/components/EmptyState";
import { toDateTimeFormat } from "@/lib/dateHelpers";
import type { CallTopicRecentCall } from "../queries";
import { formatDuration, interactionPathForCall } from "../_utils";
import RecentCallDetails from "./RecentCallDetails";

type Props = {
  calls: CallTopicRecentCall[];
  loading?: boolean;
};

function directionLabel(direction: string | null): string {
  if (direction === "inbound") return "Inbound";
  if (direction == null) return "—";
  return "Outbound";
}

const SENTIMENT_STYLES = {
  positive: { label: "Positive", tagType: "success" },
  negative: { label: "Negative", tagType: "error" },
  neutral: { label: "Neutral", tagType: "surface" },
  mixed: { label: "Mixed", tagType: "secondary" }
} as const;

type SentimentKey = keyof typeof SENTIMENT_STYLES;

function isSentimentKey(value: string): value is SentimentKey {
  return value in SENTIMENT_STYLES;
}

function sentimentTag(sentimentLabel: string | null): ReactNode {
  if (sentimentLabel == null || sentimentLabel === "") return "—";
  const key = sentimentLabel.toLowerCase();
  if (!isSentimentKey(key)) return sentimentLabel;
  const style = SENTIMENT_STYLES[key];
  return (
    <Tag tagType={style.tagType} tagStyle="standard" size="small">
      {style.label}
    </Tag>
  );
}

const COLUMN_COUNT = 9;

export default function RecentCallsCard({ calls, loading = false }: Props) {
  const navigate = useNavigate();
  const [expandedCallSid, setExpandedCallSid] = useState<string | null>(null);

  const toggleExpanded = (callSid: string) => {
    setExpandedCallSid((current) => (current === callSid ? null : callSid));
  };

  return (
    <TableCard title="Recent calls" icon="call" testId="call-topic-recent-calls">
      {loading && calls.length === 0 ? (
        <CardSkeleton />
      ) : calls.length === 0 ? (
        <EmptyState title="No calls match these filters." iconName="call" />
      ) : (
        <table className="w-full" data-testid="call-topic-recent-calls-table">
          <tbody>
            {calls.map((call) => {
              const path = interactionPathForCall(call);
              const expanded = expandedCallSid === call.callSid;
              const chevronLabel = `${expanded ? "Collapse" : "Expand"} details for ${call.topic} call`;
              return (
                <Fragment key={call.callSid}>
                  <TableCardRow
                    columnCount={COLUMN_COUNT}
                    mobile={
                      <TableCardMobileStack
                        lines={[
                          `${call.topic}${call.subtopic ? ` · ${call.subtopic}` : ""}`,
                          `${toDateTimeFormat(call.startedAt) || ""} · ${directionLabel(call.direction)} · ${formatDuration(call.durationSeconds)}`,
                          path ? (
                            <span key="link" onClick={(event) => event.stopPropagation()}>
                              <Button
                                variant="text"
                                onClick={() => navigate(path)}
                                testId={`view-interaction-${call.callSid}`}
                              >
                                View interaction
                              </Button>
                            </span>
                          ) : (
                            `${call.workerName ?? "Unknown member"} · ${expanded ? "Expanded" : "Tap row for details"}`
                          )
                        ]}
                      />
                    }
                    onClick={() => toggleExpanded(call.callSid)}
                    ariaLabel={`${call.topic} call row, ${expanded ? "expanded" : "collapsed"}`}
                    asButton={false}
                  >
                    <td className={TABLE_CARD_BODY_CELL}>
                      <span onClick={(event) => event.stopPropagation()}>
                        <IconButton
                          name="expand_more"
                          onClick={() => toggleExpanded(call.callSid)}
                          className={`transition-transform ${expanded ? "rotate-180" : ""}`}
                          ariaLabel={chevronLabel}
                          ariaExpanded={expanded}
                          skipMargin
                        />
                      </span>
                    </td>
                    <td className={TABLE_CARD_BODY_CELL}>{toDateTimeFormat(call.startedAt)}</td>
                    <td className={TABLE_CARD_BODY_CELL}>{directionLabel(call.direction)}</td>
                    <td className={TABLE_CARD_BODY_CELL}>{formatDuration(call.durationSeconds)}</td>
                    <td className={TABLE_CARD_BODY_CELL}>
                      <Tag tagType="secondary">{call.topic}</Tag>
                    </td>
                    <td className={`${TABLE_CARD_BODY_CELL} text-sys-brand-on-surface-variant`}>
                      {call.subtopic ?? "—"}
                    </td>
                    <td className={TABLE_CARD_BODY_CELL}>{call.workerName ?? "—"}</td>
                    <td className={TABLE_CARD_BODY_CELL}>{sentimentTag(call.sentimentLabel)}</td>
                    <td className={TABLE_CARD_BODY_CELL}>
                      {path ? (
                        <span onClick={(event) => event.stopPropagation()}>
                          <Button
                            variant="text"
                            onClick={() => navigate(path)}
                            testId={`view-interaction-${call.callSid}`}
                          >
                            View interaction
                          </Button>
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                  </TableCardRow>
                  {expanded && (
                    <tr
                      className="border-t border-sys-brand-surface-container-highest bg-sys-brand-surface"
                      data-testid="table-card-row-details"
                    >
                      <td colSpan={COLUMN_COUNT} className="px-4 align-top">
                        <RecentCallDetails callSid={call.callSid} hasRecording={call.recordingUrl != null} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </TableCard>
  );
}
